// src/pages/LiqA.jsx
import React from 'react';
import MEVPrice from './mevPriceReact';

// If you want global styles for this page only, import a separate CSS file
// Recommended: create src/pages/LiqA.css and import it here
// import './LiqA.css';

// For now, we'll use a <style> JSX tag (works, but not ideal for large projects)
const pageStyles = `
  body {
    font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
    line-height: 1.6;
    color: #333;
    max-width: 900px;
    margin: 0 auto;
    padding: 40px 20px;
    background-color: #f9f9f9;
  }
  header {
    text-align: center;
    margin-bottom: 40px;
  }
  h1, h2, h3 {
    color: #1a3c6d;
  }
  h1 {
    border-bottom: 2px solid #1a3c6d;
    padding-bottom: 10px;
  }
  .section {
    background: white;
    padding: 25px;
    margin-bottom: 30px;
    border-radius: 8px;
    box-shadow: 0 2px 10px rgba(0,0,0,0.08);
  }
  .image-container {
    margin: 25px 0;
    text-align: center;
  }
  .image-container img {
    max-width: 100%;
    height: auto;
    display: block;
    margin: 0 auto 10px;
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0,0,0,0.1);
  }
  .highlight {
    background-color: #fff3cd;
    padding: 15px;
    border-left: 5px solid #ffc107;
    margin: 20px 0;
  }
  footer {
    text-align: center;
    margin-top: 50px;
    color: #666;
    font-size: 0.9em;
  }
`;

export default function LiqA() {
  return (
    <>
      {/* Optional: set page title dynamically (better than <title> tag) */}
      {/* You can also use react-helmet or helmet-async for real <head> control */}
      {/* <Helmet><title>Understanding Liquidity in the MEV Project</title></Helmet> */}

      <style>{pageStyles}</style>

      <header>
        <h1>Understanding Liquidity in the MEV Project</h1>
        <p>A Guide for Our Valued Patrons</p>
      </header>

      <div className="section">
        <p>Dear MEV Patrons,</p>
        <p>
          We're thrilled to have you as part of our growing community! Over the past year, you've seen MEV evolve from a starting price of just 0.1 USD per token to <MEVPrice /> today. This growth is a testament to the value we're building together through our platform, where you earn Lc (Lead Currency) and use it to acquire MEV, which can then be held for investment or traded for BNB and converted to your local currency.
        </p>
        <p>
          As the project expands, we want to ensure everyone understands the key dynamics at play—especially <strong>liquidity</strong>—to keep things transparent and sustainable. Think of this as a shared journey: we're all in this to make MEV thrive for the long term.
        </p>
      </div>

      <div className="section">
        <h2>What is Liquidity?</h2>
        <p>
          Liquidity is one of the core pillars of our project (alongside utility, community trust, and ongoing development). In simple terms, it's the "pool" of funds available for buying and selling MEV without causing massive price swings.
        </p>
        <p>
          Our liquidity is currently around 25 BNB (equivalent to about 368,482 ZAR), made up of MEV tokens and BNB paired together in what's called a liquidity pool. This pool acts like a marketplace: when someone sells MEV, they're swapping it for BNB from the pool, and vice versa for buys.
        </p>

        <div className="image-container">
          <img
            src="https://payload-marketing.moonpay.com/api/media/file/qubu1ebsr6-7wERh37ksi26LPf3o64GuZ"
            alt="Liquidity Pool Diagram – Illustration of MEV/BNB pair with equal value sides and price curve"
          />
        </div>

        <p>
          This diagram illustrates a typical liquidity pool setup in projects like ours. The pool holds two assets (in our case, MEV and BNB). The price of MEV is determined automatically by the balance in this pool—more MEV added (from sales) means the price drops slightly to maintain equilibrium, and more buys pull it up.
        </p>
      </div>

      <div className="section">
        <h2>The Realistic Value of Your MEV Holdings</h2>
        <p>
          You might look at your wallet and see, say, 1,000 MEV worth about 8,000 USD (or 131,270 ZAR) at the current price. That's the <em>nominal value</em>—what it would be worth if sold in small amounts without affecting the market.
        </p>
        <p>
          But the <em>realistic value</em>—what you can actually liquidate without crashing the price—depends on our liquidity too. With limited liquidity, large sales can cause "slippage," where the price drops mid-transaction because there's not enough BNB in the pool to absorb the sell without adjusting the ratio.
        </p>

        <div className="image-container">
          <img
            src="https://cdn.sanity.io/images/dgybcd83/production/77445c2f27457e58406bc250c491481273cdccdb-1002x615.png"
            alt="Price Impact / Slippage Curve Graph – Showing low vs high liquidity curves"
          />
        </div>

        <p>
          This graph shows how price impact (slippage) works in a liquidity pool. The curve represents the price change as more tokens are sold. In a pool with low liquidity (like the steeper line), even a moderate sell can cause a big drop. As we grow our liquidity, the curve flattens, allowing larger trades with less impact.
        </p>
      </div>

      <div className="section">
        <h2>Comparing Holdings to Liquidity</h2>
        <p>
          To put this in perspective, let's visualize how individual holdings stack up against the total liquidity. A number of patrons hold 1,000+ MEV, and with several of such patrons, the combined nominal value could exceed our current liquidity if everyone sold at once.
        </p>

        <div className="image-container">
          <img
            src="https://thumbs.dreamstime.com/z/defi-asset-allocation-pie-chart-showing-staked-assets-farmed-assets-liquidity-pools-lending-governance-tokens-395298328.jpg"
            alt="Pie Chart – Distribution of MEV holdings vs total liquidity pool"
          />
        </div>

        <p>
          This pie chart example highlights how assets are distributed. In our case, imagine a similar chart: one slice for total liquidity (our pool's value), another for average patron holdings, and the rest for circulating supply. It shows that while your slice is valuable, the whole pie relies on balanced contributions to stay intact.
        </p>
      </div>

<div className="section">
  <h2>The Bank Analogy: Why This Isn't a Scheme</h2>
  <p>
    This setup is similar to how banks and investment firms operate under "fractional reserve" principles. Banks don't keep 100% of deposits in cash; they lend most out to grow the economy, holding just enough for daily withdrawals.
  </p>
  <p>
    If everyone demanded their money back at once (a "bank run"), even healthy banks could falter. We face the same reality: our liquidity supports steady growth and trading, but it's not designed for mass exits, which would collapse the value for all.
  </p>

  <div className="image-container">
    <img
      src="https://www.shutterstock.com/image-vector/people-run-bank-financial-crisis-260nw-1779431099.jpg"
      alt="Bank Run Illustration – Visual metaphor of panicked crowd rushing to withdraw money from a bank during a financial crisis"
    />
    <figcaption>Bank Run Metaphor: A crowd rushing to a bank in panic, illustrating the danger of mass simultaneous withdrawals</figcaption>
  </div>

  <p className="highlight">
    Just as banks use regulations and reserves to prevent chaos, we use sell limits and focus on liquidity growth to keep MEV stable. We're committed to transparency—no hidden schemes here.
  </p>
</div>

      <div className="section">
        <h2>Looking Ahead: Growing Together</h2>
        <p>
          As MEV matures, we're actively working to boost liquidity—through partnerships, more platform features, and reinvesting earnings. This means higher sell freedoms over time, more stable prices, and greater realistic value for your holdings.
        </p>
        <p>
          If everyone sells everything? The project (and token value) crumbles, benefiting no one. Instead, think long-term: hold, use, or sell gradually to let the ecosystem flourish.
        </p>
        <p>
          We value your enthusiasm and input—feel free to share thoughts or questions. Together, we're building something honest and sustainable. Let's keep the momentum going!
        </p>
      </div>

      <footer>
        <p>
          Best regards,<br />
          The MEV Team
        </p>
        <p>January 2026</p>
      </footer>
    </>
  );
}